import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  InternalIcon,
  LaptopIcon,
  Link,
  ListItemText,
  Progress,
  Stack,
  Typography,
  UnorderedList,
  UnorderedListItem,
  useTheme
} from "@carrotfertility/carotene-core"
import { useHistory } from "react-router"
import usePartnerAccess from "#/components/partner-access/hooks/usePartnerAccess"

import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { FormattedMessage, useIntl } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { Title } from "#/services/page-title/Title"
import { EnterTelemedicineForMenopauseDialog } from "#/components/telemedicine/EnterTelemedicineForMenopauseDialog"
import {
  useTelemedicineAvailability,
  TelemedicineProvider
} from "#/components/telemedicine/hooks/useTelemedicineAvailability"
import { Paths } from "#/utils/Paths"

export function TelemedicineForMenopauseLandingPage(): JSX.Element {
  const history = useHistory()
  const [openDialogFlow, setOpenDialogFlow] = useState(false)
  const intl = useIntl()
  const theme = useTheme()
  const { showPartnerAccess } = usePartnerAccess()
  const { data: telemedicineAvailability, isLoading: telemedicineAvailabilityIsLoading } = useTelemedicineAvailability()
  const isWheelAvailable =
    telemedicineAvailability?.isAvailable &&
    telemedicineAvailability?.availableProviders?.includes(TelemedicineProvider.Wheel)

  useEffect(() => {
    if (!isWheelAvailable && !telemedicineAvailabilityIsLoading) {
      history.push(Paths.PROVIDER_FINDER)
    }
  }, [history, isWheelAvailable, telemedicineAvailabilityIsLoading])

  return telemedicineAvailabilityIsLoading || !isWheelAvailable ? (
    <Box display="flex" justifyContent="center" alignItems="center" sx={{ blockSize: "100vb", inlineSize: "100vi" }}>
      <Progress />
    </Box>
  ) : (
    <PageLayout
      variant="sidebar"
      sidebar={<></>}
      header={
        <PageHeader
          startGlyph={<LaptopIcon fontSize="large" />}
          pageTitle={<FormattedMessage defaultMessage="Carrot virtual menopause clinic" />}
        />
      }
    >
      <Title title={intl.formatMessage({ defaultMessage: "Carrot virtual menopause clinic" })} />
      <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
        <Typography>
          {showPartnerAccess ? (
            <FormattedMessage defaultMessage="Carrot provides convenient, on-demand menopause telemedicine services for members who are 40-75 years old (must have irregular menstrual cycles if 40-44) and their partners." />
          ) : (
            <FormattedMessage defaultMessage="Carrot provides convenient, on-demand menopause telemedicine services for members who are 40-75 years old (must have irregular menstrual cycles if 40-44)." />
          )}
        </Typography>
        <Box>
          <Link
            component="button"
            endIcon={<InternalIcon fontSize="small" color="primary" />}
            onClick={() => setOpenDialogFlow(true)}
          >
            <FormattedMessage defaultMessage="Schedule a virtual visit with Carrot’s menopause clinic" />
          </Link>
        </Box>
        <Box
          sx={{ alignSelf: "flex-start" }}
          component="img"
          src="/images/woman-at-table.svg"
          alt="woman-at-table"
          maxHeight="180px"
        />
        <Box>
          <GetSupportUnorderedList />
          <WhoCanUseVirtualMenopauseClinic {...{ showPartnerAccess }} />
          <PayingForCareUnorderedList />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap" }} gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
          <Button onClick={() => setOpenDialogFlow(true)}>
            <FormattedMessage defaultMessage="Schedule a visit" />
          </Button>
        </Box>
      </Stack>
      <EnterTelemedicineForMenopauseDialog {...{ openDialogFlow, setOpenDialogFlow }} />
    </PageLayout>
  )
}

function GetSupportUnorderedList(): JSX.Element {
  const intl = useIntl()
  return (
    <>
      <Typography variant="h5">
        <FormattedMessage defaultMessage="Get support with:" />
      </Typography>
      <UnorderedList>
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage: "Telehealth appointments"
            })}
          />
        </UnorderedListItem>
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage: "Laboratory testing"
            })}
          />
        </UnorderedListItem>
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage: "Hormone therapy (HT) prescriptions when appropriate"
            })}
          />
        </UnorderedListItem>
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage: "Non-hormonal treatment options"
            })}
          />
        </UnorderedListItem>
      </UnorderedList>
    </>
  )
}

function WhoCanUseVirtualMenopauseClinic({ showPartnerAccess }: { showPartnerAccess: boolean }): JSX.Element {
  const intl = useIntl()
  return (
    <>
      <Typography variant="h5">
        <FormattedMessage defaultMessage="Who can use Carrot’s virtual menopause clinic" />
      </Typography>
      <UnorderedList>
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage: "Carrot members who are 40-75 years old (must have irregular menstrual cycles if 40-44)"
            })}
          />
        </UnorderedListItem>
        {showPartnerAccess ? (
          <UnorderedListItem>
            <ListItemText
              primary={intl.formatMessage(
                {
                  defaultMessage:
                    "Carrot members’ partners who are 40-75 years old (must have irregular menstrual cycles if 40-44) who have been <link>invited to join the member’s account</link>"
                },
                {
                  link: (linkContent) => (
                    <Link color="inherit" component={RouterLink} to="/account#partnerAccessFooterContainer">
                      {linkContent}
                    </Link>
                  )
                }
              )}
            />
          </UnorderedListItem>
        ) : null}
      </UnorderedList>
    </>
  )
}

function PayingForCareUnorderedList(): JSX.Element {
  const intl = useIntl()
  const { data: benefitData, isLoading: isBenefitLoading } = useBenefit()
  return (
    <>
      <Typography variant="h5">
        <FormattedMessage defaultMessage="Payment options" />
      </Typography>
      <UnorderedList>
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage:
                "Insurance: Virtual clinic providers may be in network with your insurance. You will be able to verify coverage before scheduling your first visit."
            })}
          />
        </UnorderedListItem>
        {!isBenefitLoading && benefitData.journeys.menopause.hasEligibleExpenses ? (
          <UnorderedListItem>
            <ListItemText
              primary={intl.formatMessage(
                {
                  defaultMessage:
                    "Carrot funds: You may be able to use your Carrot funds for <link>eligible menopause expenses</link> not covered by insurance. Check your benefit guide for details on menopause coverage. "
                },
                {
                  link: (linkContent) => (
                    <Link color="inherit" component={RouterLink} to="/your-benefit-guide/coverage/menopause">
                      {linkContent}
                    </Link>
                  )
                }
              )}
            />
          </UnorderedListItem>
        ) : null}
        <UnorderedListItem>
          <ListItemText
            primary={intl.formatMessage({
              defaultMessage:
                "Self pay: You can pay for your care out of pocket with a credit card or your HSA/FSA funds. Visits cost $135 for Initial and $90 for follow-ups."
            })}
          />
        </UnorderedListItem>
      </UnorderedList>
    </>
  )
}
